
import { useRef, useEffect } from "react";
import { useFaqs } from "@/hooks/useFaqs";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { Faq } from "@/types/faq-types";
import DOMPurify from "isomorphic-dompurify";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { RotateCcw } from "lucide-react";

interface FaqListProps {
  selectedFaqId?: string;
}

export const FaqList = ({ selectedFaqId }: FaqListProps) => {
  const { faqs, loading, searchQuery, refreshFaqs, hasResults } = useFaqs();
  const accordionRef = useRef<HTMLDivElement>(null);
  
  // Function to convert YouTube URL to embed URL (reused from FaqPreview)
  const getYouTubeEmbedUrl = (url: string | null) => {
    if (!url) return null;
    
    // Extract video ID from various YouTube URL formats
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    
    if (match && match[2].length === 11) {
      return `https://www.youtube.com/embed/${match[2]}`;
    }
    
    return null;
  };
  
  // Scroll to the selected FAQ
  useEffect(() => {
    if (selectedFaqId && accordionRef.current) {
      const itemElement = document.getElementById(`faq-${selectedFaqId}`);
      if (itemElement) {
        itemElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        
        // Open the accordion item
        const accordion = document.getElementById(`accordion-trigger-${selectedFaqId}`);
        if (accordion && accordion.getAttribute('data-state') !== 'open') {
          accordion.click();
        }
      }
    }
  }, [selectedFaqId, faqs]);
  
  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="border rounded-md p-4">
            <Skeleton className="h-6 w-3/4 mb-4" />
            <Skeleton className="h-4 w-full mb-2" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        ))}
      </div>
    );
  }
  
  // If search was performed but no results found
  if (searchQuery && faqs.length === 0) {
    return (
      <div className="text-center py-10">
        <div className="space-y-4">
          <p className="text-muted-foreground">No FAQs found matching "{searchQuery}".</p>
          <Button 
            variant="outline" 
            onClick={() => refreshFaqs()}
            className="mt-4"
          >
            <RotateCcw className="mr-2 h-4 w-4" />
            Show all FAQs
          </Button>
        </div>
      </div>
    );
  }
  
  // If no FAQs exist at all (when not searching)
  if (!searchQuery && faqs.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-muted-foreground">No FAQs found. Please check back later.</p>
      </div>
    );
  }
  
  return (
    <div ref={accordionRef} className="space-y-6">
      {searchQuery && faqs.length > 0 && (
        <div className="mb-6 py-2 px-4 bg-muted rounded-md text-sm flex items-center justify-between">
          <span>
            Showing {faqs.length} {faqs.length === 1 ? 'result' : 'results'} for "{searchQuery}"
          </span>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => refreshFaqs()}
            className="h-8"
          >
            <RotateCcw className="mr-2 h-3 w-3" />
            Show all
          </Button>
        </div>
      )}
      
      <Accordion type="single" collapsible className="w-full">
        {faqs.map((faq: Faq) => (
          <AccordionItem 
            key={faq.id} 
            value={faq.id}
            id={`faq-${faq.id}`}
            className="bg-white border rounded-lg mb-4 overflow-hidden shadow-sm"
          >
            <AccordionTrigger 
              id={`accordion-trigger-${faq.id}`}
              className="px-6 py-4 hover:no-underline font-medium text-left text-lg"
            >
              {faq.question}
            </AccordionTrigger>
            <AccordionContent className="px-6 pb-6 pt-2">
              <div className="flex flex-col md:flex-row gap-6">
                <div className="flex-1">
                  <div 
                    className="prose max-w-none faq-editor-content"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(faq.answer) }}
                  />
                </div>
                
                {/* Media content */}
                {(faq.image_url || faq.video_url) && (
                  <div className="flex-1 mt-4 md:mt-0 space-y-4">
                    {faq.image_url && (
                      <div>
                        <img 
                          src={faq.image_url} 
                          alt="FAQ illustration" 
                          className="rounded-md max-h-64 w-full object-contain" 
                        />
                      </div>
                    )}
                    
                    {faq.video_url && getYouTubeEmbedUrl(faq.video_url) && (
                      <div className="aspect-video">
                        <iframe
                          className="w-full h-full rounded-md"
                          src={getYouTubeEmbedUrl(faq.video_url)}
                          title="YouTube video"
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                        ></iframe>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};
